/**
 * Formatting and calendar helpers — same output as the original app
 * (dates DD/MM/YYYY, numbers with en-IN grouping, hours as "8 h").
 */
import type { ISODate } from '@/types/models';

/** `2026-09-25` → `25/09/2026`; empty → `—` */
export const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

/** `2026-09-25` → `25 September` (used in the daily report title) */
export const longDate = (iso: ISODate): string => {
  const d = new Date(iso + 'T00:00:00');
  return d.getDate() + ' ' + d.toLocaleString('en-GB', { month: 'long' });
};

/** Numbers with en-IN grouping (2,26,445); non-numeric values are returned as written. */
export const fmtNum = (v: unknown): string => {
  const n = Number(String(v).replace(/,/g, ''));
  return isNaN(n) || v === '' ? String(v) : n.toLocaleString('en-IN');
};

/** Hours, rounded to 2 decimals: `8.5 h` */
export const fmtH = (h: unknown): string => {
  const n = Number(h) || 0;
  return (Math.round(n * 100) / 100).toLocaleString('en-IN') + ' h';
};

export const isoLocal = (d: Date): ISODate =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const today = (): ISODate => isoLocal(new Date());

export const addDays = (iso: ISODate, n: number): ISODate => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return isoLocal(d);
};

/** Whole days from today to `iso` (negative = in the past); null when empty. */
export const daysLeft = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const a = new Date(iso + 'T00:00:00').getTime();
  const b = new Date(today() + 'T00:00:00').getTime();
  return Math.round((a - b) / 86400000);
};

export const isValidDate = (d: unknown): d is ISODate =>
  typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) && !isNaN(new Date(d + 'T00:00:00').getTime());

/** Days from a to b (b − a). */
export const dayDiff = (a: ISODate, b: ISODate): number =>
  Math.round((new Date(b + 'T00:00:00').getTime() - new Date(a + 'T00:00:00').getTime()) / 86400000);

export const initials = (name: string): string =>
  String(name || '')
    .split(' ')
    .map((w) => w[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

/** Sum that treats non-numbers as 0 (same as the original `sum`). */
export const sum = (a: unknown[]): number => a.reduce<number>((x, y) => x + (Number(y) || 0), 0);

export const round2 = (n: number): number => Math.round(n * 100) / 100;

/** "3 projects", "1 project" */
export const plural = (n: number, one: string, many?: string): string => `${fmtNum(n)} ${n === 1 ? one : many || one + 's'}`;

export const byDateDesc = <T extends { date: string }>(a: T, b: T): number => String(b.date || '').localeCompare(String(a.date || ''));

/** Adds https:// when a link has no scheme (as the original link form did). */
export const cleanUrl = (u: string): string => {
  const t = u.trim();
  if (!t) return '';
  return /^https?:\/\//i.test(t) ? t : 'https://' + t;
};

/** "today" / "yesterday" / "5d ago" for a past date. */
export const agoText = (iso: ISODate): string => {
  const n = -(daysLeft(iso) ?? 0);
  return n === 0 ? 'today' : n === 1 ? 'yesterday' : `${n}d ago`;
};
